import { useSession } from 'next-auth/react';
import { DateRange } from 'react-day-picker';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { fluentProvider } from '@/lib/api/providers/fluent';
import { QayaqScheduleResultApiInterface } from '@/lib/api/providers/qayaq/types';
import { API_ERROR, MAILCHIMP_EVENTS } from '@/lib/constants';
import { transformCarrierDictionary } from '@/lib/transformers/carrier';
import { transformLineDictionary } from '@/lib/transformers/line';
import { transformLocationDictionary } from '@/lib/transformers/location';
import { transformVehicleDictionary } from '@/lib/transformers/vehicle';
import { carriersState } from '@/state/carriers';
import { linesState } from '@/state/lines';
import { locationsState } from '@/state/locations';
import { getScheduleQueryByIdState, scheduleFamilyState, scheduleSearchLimitReachedState } from '@/state/schedules';
import { vehiclesState } from '@/state/vehicles';
import { AircraftSearchParams, ScheduleRequestLineQueryInterface, ScheduleRequestOceanLineQuery } from '@/types';
import { SegmentModeType } from '@/types/api-types';
import { request } from '@/utils/api/request';
import { createScheduleSearchUrl } from '@/utils/helpers';
import { logEvent } from '@/utils/logger';
import { trackUser } from '@/utils/tracking';
import { transformScheduleResults } from '@/utils/transform/transform-schedules';

interface PerformScheduleSearchArgs extends ScheduleSearchInterface {
  wasUserInitiated?: boolean;
}

interface UseScheduleContextInterface {
  performScheduleSearch: (val: PerformScheduleSearchArgs) => void;
}

interface ScheduleSearchInterface {
  fromCode: string;
  toCode: string;
  mode: SegmentModeType;
  lineQuery?: ScheduleRequestLineQueryInterface[] | ScheduleRequestOceanLineQuery[];
  aircraftParams?: AircraftSearchParams;
  dateRange?: DateRange;
  departHour?: number;
  arriveHour?: number;
  trackingId?: string;
}

interface UseSchedulesProps {
  scheduleKey: string;
  shouldUseLimited?: boolean;
}

const getSearchType = (dateRage?: DateRange): 'arrive' | 'depart' | 'dateRange' | 'unknown' => {
  if (dateRage?.from && dateRage?.to) return 'dateRange';
  if (dateRage?.from) return 'depart';
  if (dateRage?.to) return 'arrive';
  return 'unknown';
};

export const useSchedules = ({ scheduleKey, shouldUseLimited }: UseSchedulesProps): UseScheduleContextInterface => {
  const setSchedules = useSetRecoilState(scheduleFamilyState(scheduleKey));
  const setUserLimitReached = useSetRecoilState(scheduleSearchLimitReachedState);
  const [schedulesQuery, setSchedulesQuery] = useRecoilState(getScheduleQueryByIdState(scheduleKey));
  const setLocationsV2 = useSetRecoilState(locationsState);
  const setCarriersV2 = useSetRecoilState(carriersState);
  const setVehiclesV2 = useSetRecoilState(vehiclesState);
  const setLinesStateV2 = useSetRecoilState(linesState);

  // Get User data
  const { data: session } = useSession();
  const userId = session?.user.id || '';

  // Set isSearching
  const isSearchingSchedules = schedulesQuery.isSearching;

  const handleQuotaExceeded = () => {
    setUserLimitReached(true);

    // log an event that the anon search limit was hit
    trackUser.event('Schedule Search Limit Reached', {
      userId,
    });

    if (session) {
      fluentProvider.triggerCrmEvent({
        email: session.user.email!,
        eventName: MAILCHIMP_EVENTS.SCHEDULE_LIMIT_REACHED,
      });
    }
  };

  // Perform Route search and query API
  const performScheduleSearch = async ({
    dateRange,
    fromCode,
    toCode,
    lineQuery,
    mode,
    aircraftParams = undefined,
    departHour,
    arriveHour,
    wasUserInitiated = true,
    trackingId = '',
  }: PerformScheduleSearchArgs) => {
    setSchedulesQuery({
      ...schedulesQuery,
      isSearching: true,
      dateRange,
      departHour,
      arriveHour,
    });

    trackUser.event(`Schedule Search${trackingId ? `: (${trackingId})` : ''}`, {
      dateRange,
      departHour,
      arriveHour,
      wasUserInitiated,
      origin: fromCode,
      destination: toCode,
      didUseDepartHour: departHour ? departHour >= 0 : false,
      didUseArriveHour: arriveHour ? arriveHour >= 0 : false,
      searchType: getSearchType(dateRange),
    });

    try {
      if (isSearchingSchedules || (!dateRange && !shouldUseLimited) || !fromCode || !toCode) return;
      const scheduleRequestUrl = createScheduleSearchUrl(
        {
          lineQuery,
          aircraft: aircraftParams,
          dateRange,
          departHour,
          arriveHour,
          fromCode,
          toCode,
          mode,
        },
        shouldUseLimited,
      );

      // Try schedules request
      const scheduleResults = await request<QayaqScheduleResultApiInterface>(scheduleRequestUrl);

      // Transform results to desired shape
      const { carriers, routes, locations, vehicles, lines } = transformScheduleResults(scheduleResults);

      // Update/Merge existing Carriers, Locations and Vehicles State
      // V2
      setLocationsV2((previousLocations) => ({ ...previousLocations, ...transformLocationDictionary(locations) }));
      setCarriersV2((previousCarriers) => ({ ...previousCarriers, ...transformCarrierDictionary(carriers) }));
      setVehiclesV2((previousVehicles) => ({ ...previousVehicles, ...transformVehicleDictionary(vehicles) }));
      setLinesStateV2((previousLines) => ({ ...previousLines, ...transformLineDictionary(lines) }));

      // Set schedules
      setSchedules(routes);

      // GA conversion event
      trackUser.conversion({
        category: 'SeeSchedules',
        action: 'Searched',
      });
    } catch (error: any) {
      if (error.message === API_ERROR.QUOTA_EXCEEDED) {
        handleQuotaExceeded();
      } else {
        // Set empty schedules
        setSchedules([]);
        logEvent.error('Error fetching schedules', error);
      }
    } finally {
      // Turn off searching spinner
      setSchedulesQuery((prevValue) => {
        return {
          ...prevValue,
          isSearching: false,
          hasSearched: true,
        };
      });
    }
  };

  return {
    performScheduleSearch,
  };
};
